import { ProductDetail } from 'app/src/features/ProductDetail/ProductDetail'
import { CatalogCategoryBreadcrumbItem } from 'app/src/features/Showcase/components/CatalogCategoryBreadcrumbItem'
import { CatalogProductBreadcrumbItem } from 'app/src/features/Showcase/components/CatalogProductBreadcrumbItem'
import { CategoryCard } from 'app/src/features/Showcase/components/CategoryCard'
import { GeoMap } from 'app/src/features/Showcase/components/GeoMap'
import { ProductList } from 'app/src/features/Showcase/components/ProductList'
import {
  convertFromAtlasPosition,
  convertToAtlasPosition,
  getNavigatorPosition,
} from 'app/src/features/Showcase/helpers/maputils'
import {
  MapSearchMode,
  type AddressResultType,
  type GeoMapEventType,
  type PositionType,
  type ServiceCategory,
} from 'app/src/model'
import { catalogSummaryApi, companyagreementApi } from 'app/src/services/welfareportal'
import {
  DataChangedAriaLivePolite,
  InformativeBanner,
  ListItemRenderer,
  PageWidget,
  PortalInformativeDialog,
  Tag,
} from 'app/src/shared/components'
import { BreadCrumbClickableItemTemplate } from 'app/src/shared/components/BreadCrumb'
import { catalogVisibilityFunc, isDiscount } from 'app/src/shared/helpers'
import {
  useHttpClient,
  usePageTitle,
  useWebAppContext,
  useWelfarePortalNavigate,
  type FetchParamsType,
  type GotoShowCaseProps,
} from 'app/src/shared/hooks'
import * as atlas from 'azure-maps-control'
import { cloneDeep } from 'lodash'
import { AutoComplete, type AutoCompleteChangeEvent, type AutoCompleteSelectEvent } from 'primereact/autocomplete'
import { Badge } from 'primereact/badge'
import { Button } from 'primereact/button'
import { useDebounce } from 'primereact/hooks'
import type { MenuItem } from 'primereact/menuitem'
import { ProgressSpinner } from 'primereact/progressspinner'
import { RadioButton } from 'primereact/radiobutton'
import { TabMenu } from 'primereact/tabmenu'

import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isProductListItem = (model: any): model is companyagreementApi.ProductListItem => {
  return typeof model === 'object' && Boolean(model.productName)
}
export function Showcase(): ReactElement {
  const context = useWebAppContext()
  const httpClient = useHttpClient()
  const [searchParams] = useSearchParams()
  const [serviceCategoryModalVisible, setServiceCategoryModalVisible] = useState<boolean>(false)
  const macroCategoryId = searchParams.get('macroCategoryId') ?? ''
  const selectedServiceCategory: ServiceCategory = (searchParams.get('serviceCategory') ?? undefined) as ServiceCategory
  const appBarT = useTranslation('home', { keyPrefix: 'AppBar' })
  const { t } = useTranslation('catalog', { keyPrefix: 'Catalog.Showcase' })
  const globalT = useTranslation('home')

  usePageTitle(appBarT.t('Catalog'))

  const catalogSummaryDetails = catalogSummaryApi.useCatalogSummaryList({
    pathParams: { templateBasePath: context.userInfo.welfareCampaign.configuration.frontend.templateId },
  })

  const autocompleteRef = useRef<AutoComplete>(null)
  const categoryFromURL = searchParams.get('selectedCategory') ?? macroCategoryId
  const breadcrumbFromURL = searchParams.get('breadcrumb')
  const selectedProductFromURL = searchParams.get('selectedProduct')
  const fromFavourites = searchParams.get('favourites') === 'true'
  const discount = isDiscount(searchParams)
  const [autoCompleteSearchText, autoCompleteSearchTextDebounced, setAutoCompleteSearchText] = useDebounce<string | undefined>(
    '',
    300,
  )

  const [suggestions, setSuggestions] = useState<(AddressResultType | companyagreementApi.ProductListItem)[]>([])
  const navigate = useWelfarePortalNavigate()

  const [categoryItems, setCategoryItems] = useState<companyagreementApi.Category[]>()
  const [productItems, setProductItems] = useState<companyagreementApi.ProductListItem[]>()
  const [dataSetChanged, setDataSetChanged] = useState<boolean>(false)
  const [serviceCategorySearchBadges, setServiceCategorySearchBadges] = useState<{ [key: string]: number }>()

  // MAP
  const apiConfiguration = companyagreementApi.useConfigurationGet({})

  const [defaultPosition, setDefaultPosition] = useState<atlas.data.Position>()
  const [navigatorPosition, setNavigatorPosition] = useState<atlas.data.Position>()
  const [breadcrumbProductName, setBreadcrumbProductName] = useState<string | null>(null)

  const getFirstItemBreadcrumb = () => {
    const catalogName =
      catalogSummaryDetails.data?.categories?.find((x) => x.id === macroCategoryId && catalogVisibilityFunc(x, context, false))
        ?.title ?? ''
    return catalogName
  }

  const handleDefaultPosition = useCallback(() => {
    if (context.portalConfiguration.showCaseConfig?.mapsConfig) {
      const restoreDefaultPosition = () =>
        setDefaultPosition(
          new atlas.data.Position(
            context.portalConfiguration.showCaseConfig?.mapsConfig?.initialCords?.lon ?? 0,
            context.portalConfiguration.showCaseConfig?.mapsConfig?.initialCords?.lat ?? 0,
          ),
        )
      if (context.portalConfiguration.showCaseConfig.mapsConfig.useNavigatorPosition) {
        getNavigatorPosition(
          (address: AddressResultType) => {
            const position = convertToAtlasPosition({ latitude: address?.position?.lat, longitude: address?.position?.lon })
            setDefaultPosition(position)
            setNavigatorPosition(position)
          },
          (positionError: GeolocationPositionError) => {
            //FALLBack on Default
            context.logger?.error('Error getting Navigator Geolocation', { positionError })
            restoreDefaultPosition()
          },
        )
      } else restoreDefaultPosition()
    } else return undefined
  }, [context.logger, context.portalConfiguration?.showCaseConfig?.mapsConfig])

  const fetchAtlasAddresses = useCallback(
    async (query?: string, basePosition?: PositionType): Promise<AddressResultType[] | undefined> => {
      if (!query || apiConfiguration.isError || !apiConfiguration.data) return []
      let requestPath = `${context.portalConfiguration.showCaseConfig?.mapsConfig?.atlasAddress}/search/address/json?api-version=${context.portalConfiguration.showCaseConfig?.mapsConfig?.atlasApiVersion ?? '1.0'}&language=${globalT.i18n?.language ?? 'it-IT'}`
      if (context.portalConfiguration.showCaseConfig?.mapsConfig?.mapCountry)
        requestPath = `${requestPath}&countrySet=${context.portalConfiguration.showCaseConfig?.mapsConfig?.mapCountry}`
      requestPath = `${requestPath}&query=${query}`

      if (basePosition) requestPath = `${requestPath}&lat=${basePosition.lat}&lon=${basePosition.lon}`

      const mapClientId = apiConfiguration?.data?.mapId && !apiConfiguration?.isError ? apiConfiguration.data.mapId : undefined

      const requestHeaders: HeadersInit = new Headers()
      requestHeaders.set('X-Ms-Client-ID', mapClientId ?? '')
      if (context.portalConfiguration?.showCaseConfig?.mapsConfig?.atlasAuthenticationMode === 'anonymous') {
        requestHeaders.set('Authorization', `Bearer ${apiConfiguration.data.mapToken}`)
      } else if (context.portalConfiguration?.showCaseConfig?.mapsConfig?.atlasAuthenticationMode === 'subscriptionKey') {
        requestHeaders.set(
          'Subscription-Key',
          context.portalConfiguration.showCaseConfig.mapsConfig.atlasAuthenticationSubscriptionKey ?? '',
        )
      }
      const fetchParams: FetchParamsType = {
        input: requestPath,
        init: { headers: requestHeaders },
      }
      const jsonResp = await httpClient.executeJson<{ results: AddressResultType[] }>(fetchParams)

      if (jsonResp.httpResponse?.status === 401) {
        context.logger?.error('Map Token Expired, refetching')
        await apiConfiguration.refetch()
      }

      if (!jsonResp.fetchError && !jsonResp.jsonConversionError) {
        return jsonResp.jsonResponse!.results
      } else return []
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      context.portalConfiguration.showCaseConfig?.mapsConfig,
      apiConfiguration?.data?.mapId,
      apiConfiguration?.data?.mapToken,
      apiConfiguration?.isError,
    ],
  )

  const [lastGeoMapEvent, setLastGeoMapEvent] = useState<GeoMapEventType>()
  const [mapSearchMode, setMapSearchMode] = useState<MapSearchMode>(MapSearchMode.ADDRESS)
  const [filters, setFilters] = useState<{
    selectedCategory?: string
    serviceCategory?: ServiceCategory
    categoriesLimit?: number
    categoriesOffset?: number
    productsLimit?: number
    productsOffset?: number
    searchText?: string
    mapParams?: {
      locationRadius: number
      currentPosition: AddressResultType
      selectedAddress?: AddressResultType
    }
  }>({
    selectedCategory: categoryFromURL,
    serviceCategory: selectedServiceCategory,
  })

  const getServiceCategory = useCallback(() => {
    return !discount ? filters?.serviceCategory : 'none'
  }, [discount, filters?.serviceCategory])

  const getCatalog = useCallback(() => {
    return discount ? 'discount' : 'welfare'
  }, [discount])

  const currentMacroCategory = useMemo(
    () =>
      catalogSummaryDetails?.data?.categories?.find((x) => x.id === macroCategoryId && catalogVisibilityFunc(x, context, false)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [macroCategoryId, catalogSummaryDetails],
  )
  const categories = companyagreementApi.useCategoryList<companyagreementApi.CategoryPaginated>(
    {
      queryParams: {
        parentCategoryId: filters?.selectedCategory,
        serviceCategory: getServiceCategory(),
        includeChildren: macroCategoryId === filters?.selectedCategory ? false : undefined,
        pagingLimit: filters?.categoriesLimit ?? context.portalConfiguration.paginatorConfig.hugeInitialPageSize ?? 1000,
        pagingOffset: filters?.categoriesOffset ?? 0,
        catalog: getCatalog(),
      },
    },
    {
      enabled:
        !selectedProductFromURL &&
        filters?.selectedCategory !== undefined &&
        ((filters?.serviceCategory !== null && filters?.serviceCategory !== undefined) ||
          !currentMacroCategory?.serviceCategoryConfigurations ||
          currentMacroCategory.serviceCategoryConfigurations.length == 0),
    },
  )

  const currentCategory = companyagreementApi.useCategoryGet<companyagreementApi.Category>(
    {
      pathParams: { categoryId: categoryFromURL },
    },
    {
      enabled: categoryFromURL != undefined,
    },
  )
  const showMap: boolean = useMemo(
    () =>
      (typeof context.portalConfiguration?.showCaseConfig?.mapsConfig?.enableMap !== 'undefined' &&
        context.portalConfiguration.showCaseConfig.mapsConfig.enableMap &&
        currentCategory?.data?.displayMode &&
        selectedServiceCategory &&
        currentCategory?.data?.displayMode[selectedServiceCategory] === 'map' &&
        !apiConfiguration?.isError &&
        typeof apiConfiguration?.data?.mapId !== 'undefined') ??
      false,
    [
      apiConfiguration?.data?.mapId,
      apiConfiguration?.isError,
      context.portalConfiguration?.showCaseConfig?.mapsConfig?.enableMap,
      currentCategory?.data?.displayMode,
      selectedServiceCategory,
    ],
  )
  const products = companyagreementApi.useProductList<companyagreementApi.ProductPaginated>(
    {
      queryParams: {
        serviceCategory: getServiceCategory(),
        categoryId: filters?.selectedCategory,
        key: !showMap || mapSearchMode == MapSearchMode.PRODUCTNAME ? filters?.searchText : undefined,
        pagingLimit: filters?.productsLimit ?? context.portalConfiguration.paginatorConfig.initialPageSize,
        pagingOffset: filters?.productsOffset ?? 0,
        includeSubCategories: filters.searchText ? true : false,
        ...(showMap
          ? {
              locationLatitude: filters?.mapParams?.selectedAddress?.position
                ? filters.mapParams.selectedAddress.position.lat
                : filters?.mapParams?.currentPosition?.position?.lat,
              locationLongitude: filters?.mapParams?.selectedAddress?.position
                ? filters.mapParams.selectedAddress.position.lon
                : filters?.mapParams?.currentPosition?.position?.lon,
              locationRadius: filters?.mapParams?.locationRadius,
            }
          : {}),
        catalog: getCatalog(),
      },
    },
    {
      enabled:
        !selectedProductFromURL &&
        filters?.selectedCategory !== undefined &&
        ((filters?.serviceCategory !== null && filters?.serviceCategory !== undefined) ||
          !currentMacroCategory?.serviceCategoryConfigurations ||
          currentMacroCategory.serviceCategoryConfigurations.length == 0),
    },
  )

  useEffect(() => {
    if (showMap && defaultPosition && !filters?.mapParams?.currentPosition) {
      const filters_ = cloneDeep(filters)
      if (!filters_.mapParams)
        filters_.mapParams = {
          currentPosition: { position: convertFromAtlasPosition(defaultPosition) },
          locationRadius: context.portalConfiguration.showCaseConfig?.mapsConfig?.atlasInitialFreeLocationRadius ?? 0,
        }

      setFilters(filters_)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPosition, showMap, filters])

  useEffect(() => {
    if (showMap) handleDefaultPosition()
  }, [handleDefaultPosition, showMap])

  const [addressResultList, setAddressResultList] = useState<AddressResultType[]>()

  useEffect(() => {
    const worker = async () => {
      if (showMap && mapSearchMode === MapSearchMode.ADDRESS) {
        setAddressResultList(
          autoCompleteSearchTextDebounced
            ? await fetchAtlasAddresses(autoCompleteSearchTextDebounced, filters?.mapParams?.selectedAddress?.position)
            : [],
        )
      }
    }
    worker()
  }, [
    fetchAtlasAddresses,
    autoCompleteSearchTextDebounced,
    showMap,
    mapSearchMode,
    filters?.mapParams?.selectedAddress?.position,
  ])

  useEffect(() => {
    if (!showMap) {
      setFilters((prev) => ({ ...prev, mapParams: undefined, searchText: undefined }))
      setAutoCompleteSearchText(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMap])

  const productsAutocomplete = companyagreementApi.useProductList<companyagreementApi.ProductPaginated>(
    {
      queryParams: {
        categoryId: filters?.selectedCategory,
        key: autoCompleteSearchTextDebounced,
        includeSubCategories: true,
        catalog: getCatalog(),
      },
    },
    {
      enabled:
        (!showMap || mapSearchMode === MapSearchMode.PRODUCTNAME) &&
        autoCompleteSearchTextDebounced !== undefined &&
        autoCompleteSearchTextDebounced !== '' &&
        autoCompleteSearchTextDebounced.length > 2,
    },
  )

  const currentServiceConfigurationItem = useMemo(() => {
    return currentMacroCategory?.serviceCategoryConfigurations?.find((x) => x.key === filters?.serviceCategory)
  }, [filters?.serviceCategory, currentMacroCategory])

  // CategoryPaginated
  useEffect(() => {
    if (categories && !categories.isError && categories.data?.paging) {
      setCategoryItems([...((categories.data.paging.offset > 0 ? categoryItems : []) ?? []), ...(categories.data?.items ?? [])])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories.data])

  //ProductsPaginated
  useEffect(() => {
    if (products && !products.isError && products.data?.paging && products.data.paging.offset > 0) {
      setProductItems([...(productItems ?? []), ...(products.data?.items ?? [])])
    } else if (products.data?.items && products.data?.paging?.offset === 0) {
      setProductItems(products.data.items)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products.data])

  //Search Autocomplete
  useEffect(() => {
    if (!filters.searchText) setAutoCompleteSearchText(undefined)
    else autocompleteRef?.current?.hide()
  }, [filters.searchText, autocompleteRef, setAutoCompleteSearchText])

  const goToShowcase = useCallback(
    (callbackProps: Partial<GotoShowCaseProps>) => {
      setCategoryItems(undefined)
      setProductItems(undefined)
      navigate.goToShowcase(callbackProps)
    },
    [navigate],
  )

  //Badge
  useEffect(() => {
    const worker = async () => {
      if (
        currentMacroCategory?.id === filters?.selectedCategory &&
        currentMacroCategory?.serviceCategoryConfigurations &&
        currentMacroCategory?.serviceCategoryConfigurations?.length > 0 &&
        filters.searchText
      ) {
        const productPaginatedResponses: { [key: string]: number } = {}
        setServiceCategorySearchBadges(undefined)
        let tmpNumb: number
        for (let index = 0; index < currentMacroCategory.serviceCategoryConfigurations.length; index++) {
          try {
            tmpNumb =
              (
                await companyagreementApi.fetchProductList({
                  queryParams: {
                    categoryId: currentMacroCategory?.id,
                    includeSubCategories: true,
                    serviceCategory: currentMacroCategory.serviceCategoryConfigurations[index].key as unknown as ServiceCategory,
                    key: filters?.searchText,
                  },
                })
              ).paging?.total ?? 0
          } catch (err) {
            tmpNumb = 0
            context.showToastMessage({ content: t('ErrorSearch'), severity: 'error' })
            context.logger?.error('Error calculating Service Category Badge', { error: err })
          }
          productPaginatedResponses[currentMacroCategory.serviceCategoryConfigurations[index].key] = tmpNumb
        }
        setServiceCategorySearchBadges(productPaginatedResponses)
      }
    }

    worker()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.searchText, filters.selectedCategory]) // }, [filters.searchText, currentMacroCategory, filters.selectedCategory])

  useEffect(() => {
    const breadCrumbItems: MenuItem[] = []
    const firstLabel = discount ? t('Discount') : appBarT.t('Catalog')
    const firstCommand = discount
      ? () => {
          navigate.goToDiscount()
          setFilters({ ...filters, searchText: undefined })
        }
      : () => navigate.goToCatalog()
    breadCrumbItems.push({
      template: () => BreadCrumbClickableItemTemplate({ label: firstLabel, command: firstCommand }),
    })
    if (
      catalogSummaryDetails.data?.categories?.find(
        (x) => x.id === macroCategoryId && macroCategoryId !== 'DISCOUNT' && catalogVisibilityFunc(x, context, false),
      )?.title
    ) {
      breadCrumbItems.push({
        template: () =>
          BreadCrumbClickableItemTemplate({
            label: getFirstItemBreadcrumb(),
            disabled: products?.isFetching || categories?.isFetching,
            command:
              macroCategoryId !== filters.selectedCategory || selectedProductFromURL
                ? () =>
                    goToShowcase({
                      macroCategoryId,
                      selectedCategory: macroCategoryId,
                      serviceCategory: getServiceCategory(),
                    })
                : undefined,
          }),
      })
    }
    let breadcrumbArray: string[] = []
    if (breadcrumbFromURL) {
      breadcrumbArray = [...breadcrumbFromURL.split('::')]
    }

    const newBreadcrumb = breadcrumbArray.map((item, index) => {
      const breadcrumbPath = breadcrumbArray.slice(0, index + 1).join('::')
      return {
        template: () => (
          <CatalogCategoryBreadcrumbItem
            categoryId={item}
            command={
              index + 1 < breadcrumbArray.length || selectedProductFromURL
                ? () =>
                    goToShowcase({
                      macroCategoryId,
                      serviceCategory: getServiceCategory(),
                      selectedCategory: item,
                      breadCrumb: breadcrumbPath,
                    })
                : undefined
            }
          />
        ),
      }
    })

    const productBreadcrumb: MenuItem[] = []
    if (selectedProductFromURL) {
      if (fromFavourites) {
        productBreadcrumb.push({
          template: () =>
            BreadCrumbClickableItemTemplate({
              label: t('Favourites'),
              command: () => navigate.goToFavourites(),
            }),
        })
      }
      productBreadcrumb.push({
        template: () => <CatalogProductBreadcrumbItem productName={breadcrumbProductName ?? null} />,
      })
    }

    context.update({ breadCrumbItems: breadCrumbItems.concat(newBreadcrumb).concat(productBreadcrumb) })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    breadcrumbFromURL,
    filters,
    macroCategoryId,
    catalogSummaryDetails.data,
    products?.isFetching,
    categories?.isFetching,
    selectedProductFromURL,
    breadcrumbProductName,
  ])

  useEffect(() => {
    if (categoryFromURL)
      setFilters((prev) => ({
        ...prev,
        serviceCategory:
          !selectedServiceCategory &&
          currentMacroCategory?.serviceCategoryConfigurations &&
          currentMacroCategory.serviceCategoryConfigurations.length > 0
            ? (currentMacroCategory.serviceCategoryConfigurations[0].key as ServiceCategory)
            : selectedServiceCategory,
        selectedCategory: categoryFromURL,
        categoriesLimit: undefined,
        categoriesOffset: undefined,
        productsLimit: undefined,
        productsOffset: undefined,
      }))
  }, [searchParams, categoryFromURL, selectedServiceCategory, currentMacroCategory])

  //Refilter Click su BreadCrumb
  useEffect(() => {
    if (selectedProductFromURL) setFilters({ ...filters, selectedCategory: undefined })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductFromURL])

  useEffect(() => {
    if (lastGeoMapEvent) {
      switch (lastGeoMapEvent.evt?.type) {
        case 'dragend':
        case 'zoomend': {
          const locationRadius = filters?.mapParams?.selectedAddress
            ? (context.portalConfiguration.showCaseConfig?.mapsConfig?.atlasInitialFreeLocationRadius ?? 0)
            : lastGeoMapEvent.locationRadius <= (context.portalConfiguration.showCaseConfig?.mapsConfig?.maxLocationRadius ?? 0)
              ? lastGeoMapEvent.locationRadius
              : (context.portalConfiguration.showCaseConfig?.mapsConfig?.maxLocationRadius ?? 0)

          const center = lastGeoMapEvent.evt.map.getCamera().center
          const currentPosition: AddressResultType = {
            position: convertFromAtlasPosition(center!),
            radius: locationRadius,
            boundingBox: lastGeoMapEvent.evt.map.getCamera().bounds,
          }
          const _filters = cloneDeep(filters)
          _filters.mapParams = {
            ..._filters.mapParams,
            locationRadius,
            currentPosition,
          }
          setFilters(_filters)
          break
        }
        default:
          break
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastGeoMapEvent])

  useEffect(() => {
    setDataSetChanged(true)
    setTimeout(() => {
      setDataSetChanged(false)
    }, 3000)
  }, [products.data, categories.data])

  useEffect(() => {
    if (showMap && mapSearchMode === MapSearchMode.ADDRESS) setSuggestions(addressResultList ?? [])
    else if (productsAutocomplete?.data?.items) setSuggestions(productsAutocomplete?.data?.items)
  }, [productsAutocomplete?.data?.items, showMap, mapSearchMode, addressResultList])

  const lastTokenFetchTimeRef = useRef<number>(0)
  const MIN_TOKEN_REFRESH_INTERVAL_MS = 30_000

  const getMapToken: atlas.getAuthTokenCallback = useCallback(
    (resolve: (value?: string) => void): void => {
      const now = Date.now()
      if (now - lastTokenFetchTimeRef.current < MIN_TOKEN_REFRESH_INTERVAL_MS) {
        resolve(apiConfiguration.data?.mapToken)
        return
      }
      lastTokenFetchTimeRef.current = now
      apiConfiguration.refetch().then((configurationResponse) => {
        resolve(configurationResponse.data?.mapToken)
      })
    },
    [apiConfiguration],
  )

  if (selectedProductFromURL)
    return (
      <PageWidget>
        <ProductDetail
          productId={selectedProductFromURL!}
          macroCategoryId={macroCategoryId}
          apiConfiguration={!apiConfiguration?.isError ? apiConfiguration?.data : undefined}
          parentSelectedCategoryItem={!currentCategory.isError ? currentCategory?.data : undefined}
          setProductName={setBreadcrumbProductName}
        />
      </PageWidget>
    )
  else
    return (
      <PageWidget>
        {serviceCategoryModalVisible &&
          currentServiceConfigurationItem?.informativeBanner &&
          catalogSummaryDetails.data?.informativeBanners?.[currentServiceConfigurationItem?.informativeBanner]?.modal && (
            <PortalInformativeDialog
              modal={catalogSummaryDetails.data?.informativeBanners?.[currentServiceConfigurationItem?.informativeBanner].modal}
              onHide={() => setServiceCategoryModalVisible(false)}
            />
          )}
        {currentMacroCategory?.serviceCategoryConfigurations && (
          <>
            {macroCategoryId !== filters?.selectedCategory && filters?.serviceCategory && (
              <div>
                <Tag tag={currentServiceConfigurationItem?.tabberName ?? ''} />
              </div>
            )}
          </>
        )}
        {showMap && (
          <div className='card justify-content-center my-3 flex'>
            <div className='flex flex-wrap gap-3'>
              <div className='align-items-center flex'>
                <span dangerouslySetInnerHTML={{ __html: t('Map.searchByLabel') }}></span>
              </div>
              <div className='align-items-left m-auto flex'>
                <RadioButton
                  inputId='address'
                  name='mapsearchmode'
                  value={MapSearchMode.ADDRESS}
                  pt={{ root: { className: 'm-auto' } }}
                  disabled={typeof filters?.searchText !== 'undefined'}
                  onChange={(e) => setMapSearchMode(e.value)}
                  checked={mapSearchMode === MapSearchMode.ADDRESS}
                />
                <label htmlFor='address' className='ml-2' dangerouslySetInnerHTML={{ __html: t('Map.searchByAddress') }}></label>
              </div>
              <div className='align-items-center flex'>
                <RadioButton
                  inputId='productname'
                  name='mapsearchmode'
                  value={MapSearchMode.PRODUCTNAME}
                  pt={{ root: { className: 'm-auto' } }}
                  disabled={typeof filters?.searchText !== 'undefined'}
                  onChange={(e) => setMapSearchMode(e.value)}
                  checked={mapSearchMode === MapSearchMode.PRODUCTNAME}
                />
                <label
                  htmlFor='productname'
                  className='ml-2'
                  dangerouslySetInnerHTML={{ __html: t('Map.searchByProductName') }}
                ></label>
              </div>
            </div>
          </div>
        )}

        <div className='card p-fluid mb-1 mt-1 flex'>
          <AutoComplete
            value={autoCompleteSearchText}
            inputId='search_autocomplete'
            ref={autocompleteRef}
            onChange={(evt: AutoCompleteChangeEvent) => {
              setAutoCompleteSearchText(typeof evt.value === 'string' ? evt.value : evt.value?.name)

              if (Array.isArray(evt.value) && evt.value.length === 0) {
                const filters_ = cloneDeep(filters)
                if (filters_.mapParams) {
                  filters_.mapParams.currentPosition = { position: convertFromAtlasPosition(defaultPosition!) }
                  filters_.mapParams.selectedAddress = undefined
                }
                filters_.searchText = undefined
                setFilters(filters_)
              }
            }}
            maxLength={100}
            completeMethod={(evt) => {
              setAutoCompleteSearchText(evt.query)
            }}
            onKeyPress={(evt) => {
              if ((!showMap || mapSearchMode === MapSearchMode.PRODUCTNAME) && evt.key === 'Enter') {
                setFilters({ ...filters, searchText: autoCompleteSearchText })
              }
            }}
            pt={{
              root: {
                className: 'w-[100%] mb-4',
              },
              input: {
                root: {
                  className: 'rounded-r-none',
                },
              },
              container: {
                className: 'rounded-r-none',
              },
              inputToken: {
                className: typeof filters?.searchText !== 'undefined' ? 'hidden' : '',
              },
              loadingIcon: {
                className: productsAutocomplete?.isLoading || categories?.isLoading || products?.isLoading ? 'grid' : 'hidden',
              },
              removeTokenIcon: {
                role: 'button',
                tabIndex: 0,
                'aria-label': t('CloseSearch'),
                onKeyDown: (evt: { key: string }) => {
                  if (evt.key === ' ' || evt.key === 'Enter') {
                    setFilters({ ...filters, searchText: undefined })
                    setAutoCompleteSearchText('')
                  }
                },
              },
            }}
            field={showMap && mapSearchMode === MapSearchMode.ADDRESS ? 'address.freeFormAddress' : 'productName'}
            onSelect={(evt: AutoCompleteSelectEvent<AddressResultType | companyagreementApi.ProductListItem>) => {
              if (!showMap) {
                // se non sono in modalità showMap allora sto trattando un ProductListItem
                const product: companyagreementApi.ProductListItem = evt.value as companyagreementApi.ProductListItem
                goToShowcase({
                  macroCategoryId,
                  serviceCategory: getServiceCategory(),
                  breadCrumb: breadcrumbFromURL ?? '',
                  selectedProduct: product.productId,
                })
              } else {
                // ...altrimenti sto trattando la mappa

                // imposto i filtri, se non ce ne sono ci metto quelli di default che scendono dalla configurazione
                const filters_ = cloneDeep(filters)
                if (!filters_.mapParams) {
                  filters_.mapParams = {
                    locationRadius: context.portalConfiguration.showCaseConfig?.mapsConfig?.atlasInitialFreeLocationRadius ?? 0,
                    currentPosition: { position: convertFromAtlasPosition(defaultPosition!) },
                  }
                }
                if (mapSearchMode === MapSearchMode.ADDRESS) {
                  const addressResult: AddressResultType = evt.value as AddressResultType
                  setAutoCompleteSearchText(addressResult.address?.freeformAddress)

                  filters_.mapParams!.selectedAddress = addressResult
                  filters_.mapParams!.currentPosition = addressResult
                  filters_.searchText = addressResult.address?.freeformAddress
                } else {
                  // altrimenti MapSearchMode è PRODUCTNAME
                  const addressResult: companyagreementApi.ProductListItem = evt.value as companyagreementApi.ProductListItem
                  filters_.mapParams!.selectedAddress = {
                    position: {
                      lat: addressResult.geoLocation?.latitude ?? 0,
                      lon: addressResult.geoLocation?.longitude ?? 0,
                    },
                  }
                  filters_.mapParams!.currentPosition = filters_.mapParams!.selectedAddress
                  filters_.searchText = addressResult.productName
                }
                setFilters(filters_)
              }
            }}
            placeholder={discount ? t('SearchDiscountPlaceholder') : t('SearchPlaceholder')}
            itemTemplate={(suggestion: companyagreementApi.ProductListItem | AddressResultType) => {
              return isProductListItem(suggestion) ? (
                showMap ? (
                  <div>
                    <div className='line-clamp-1 font-bold'>{suggestion.productName}</div>
                    {suggestion.geoAddress && <div className='line-clamp-1'>{suggestion.geoAddress}</div>}
                  </div>
                ) : (
                  <div>
                    <div className='line-clamp-1'>{suggestion.productName}</div>
                  </div>
                )
              ) : (
                <p>{suggestion.address?.freeformAddress}</p>
              )
            }}
            suggestions={suggestions}
          />
          <div>
            <Button
              icon='pi pi-search'
              pt={{ root: { className: 'rounded-s-none', 'aria-label': 'search-button' } }}
              disabled={autoCompleteSearchText === 'undefined' || products?.isFetching || categories?.isFetching}
              onClick={() => {
                if (!showMap || mapSearchMode === MapSearchMode.PRODUCTNAME)
                  setFilters({ ...filters, searchText: autoCompleteSearchText })
              }}
            ></Button>
          </div>
        </div>
        {dataSetChanged && <DataChangedAriaLivePolite message={t('DataSetChanged')} />}
        {filters.selectedCategory === macroCategoryId &&
          currentMacroCategory?.serviceCategoryConfigurations &&
          currentMacroCategory.serviceCategoryConfigurations.length > 0 &&
          !discount && (
            <>
              <TabMenu
                pt={{
                  root: {
                    role: 'tablist',
                  },
                  menu: {
                    role: 'none',
                  },
                  menuitem: {
                    role: 'tab',
                  },
                  action: {
                    role: 'none',
                  },
                }}
                model={currentMacroCategory.serviceCategoryConfigurations.map((x) => ({
                  label: x.tabberName,
                  disabled: products?.isFetching || categories?.isFetching,
                  template(_item, options) {
                    return (
                      <div className='flex'>
                        {options.element}
                        {filters?.searchText && (
                          <Badge value={serviceCategorySearchBadges?.[x.key] ?? '..'} severity={'danger'} />
                        )}
                      </div>
                    )
                  },
                }))}
                onTabChange={(e) => {
                  if (e.index !== currentServiceConfigurationItem?.index) {
                    goToShowcase({
                      macroCategoryId,
                      serviceCategory:
                        (currentMacroCategory.serviceCategoryConfigurations?.find((x) => x.index === e.index)
                          ?.key as ServiceCategory) ?? 'none',
                    })
                  }
                }}
                activeIndex={currentServiceConfigurationItem?.index}
              />
              {currentServiceConfigurationItem?.description && (
                <p className='mt-10'>{currentServiceConfigurationItem?.description}</p>
              )}
            </>
          )}
        {currentServiceConfigurationItem?.informativeBanner &&
        filters?.selectedCategory === macroCategoryId &&
        catalogSummaryDetails?.data?.informativeBanners?.[currentServiceConfigurationItem?.informativeBanner] &&
        !discount ? (
          <div className='mt-3'>
            <InformativeBanner
              inlineIcon
              text={
                <div>
                  <div
                    className='text-base'
                    dangerouslySetInnerHTML={{
                      __html:
                        catalogSummaryDetails.data.informativeBanners[currentServiceConfigurationItem?.informativeBanner]
                          ?.content,
                    }}
                  />

                  {catalogSummaryDetails.data.informativeBanners[currentServiceConfigurationItem?.informativeBanner]
                    ?.actionLabel && (
                    <div>
                      <Button
                        link
                        text
                        pt={{ root: { className: 'underline pb-0  pl-0' } }}
                        icon={
                          catalogSummaryDetails.data.informativeBanners[currentServiceConfigurationItem?.informativeBanner]
                            .actionLabelIcon
                        }
                        onClick={() => setServiceCategoryModalVisible(true)}
                      >
                        {
                          catalogSummaryDetails.data.informativeBanners[currentServiceConfigurationItem?.informativeBanner]
                            ?.actionLabel
                        }
                      </Button>
                    </div>
                  )}
                </div>
              }
            />
          </div>
        ) : (
          <></>
        )}
        {showMap && (
          <div>
            <GeoMap
              address={filters?.mapParams?.selectedAddress}
              defaultPosition={defaultPosition}
              positionRestoreCallBack={handleDefaultPosition}
              productGridList={productItems!}
              onProductClick={(product: companyagreementApi.ProductListItem) => {
                goToShowcase({
                  macroCategoryId,
                  serviceCategory: getServiceCategory(),
                  breadCrumb: breadcrumbFromURL ?? '',
                  selectedProduct: product.productId,
                  selectedCategory: filters?.selectedCategory,
                })
              }}
              onMapEvent={setLastGeoMapEvent}
              authOptions={{
                clientId: apiConfiguration?.data?.mapId,
                authType: context.portalConfiguration.showCaseConfig?.mapsConfig
                  ?.atlasAuthenticationMode as atlas.AuthenticationType,
                subscriptionKey: context.portalConfiguration.showCaseConfig?.mapsConfig?.atlasAuthenticationSubscriptionKey,
                getToken:
                  context.portalConfiguration.showCaseConfig?.mapsConfig?.atlasAuthenticationMode === 'anonymous'
                    ? getMapToken
                    : undefined,
              }}
              mapConfig={context.portalConfiguration.showCaseConfig?.mapsConfig}
            />
          </div>
        )}
        {!filters?.searchText &&
        categoryItems &&
        categoryItems?.length > 0 &&
        Array.isArray(products.data?.items) &&
        products.data?.items.length == 0 ? (
          <ListItemRenderer
            pt={{
              container: {
                className: 'grid gap-4 grid-cols-1 md:grid-cols-3 lg:grid-cols-4',
              },
              paginator: {
                renderedInitialPageSize: context.portalConfiguration.paginatorConfig.hugeRenderedInitialPageSize ?? 1000,
                initialPageSize: context.portalConfiguration.paginatorConfig.hugeInitialPageSize ?? 1000,
              },
              autofocus: {
                disabled: true,
              },
            }}
            data={{ paging: categories?.data?.paging, items: categoryItems }}
            loadMore={(limit: number, offset: number) => {
              setFilters({
                ...filters,
                categoriesLimit: limit,
                categoriesOffset: offset,
              })
            }}
            loading={categories?.isLoading || products?.isLoading}
            renderItem={(item) => (
              <CategoryCard
                macroCategoryId={macroCategoryId}
                category={item}
                onSelection={() => {
                  goToShowcase({
                    macroCategoryId,
                    serviceCategory: getServiceCategory(),
                    selectedCategory: item.categoryId,
                    breadCrumb: `${breadcrumbFromURL ? `${breadcrumbFromURL}::${item.categoryId}` : item.categoryId}`,
                  })
                }}
              ></CategoryCard>
            )}
          />
        ) : ((productItems && productItems?.length) ?? 0) > 0 ? (
          <>
            <ProductList
              products={{
                paging: showMap && filters.mapParams ? undefined : products?.data?.paging,
                items:
                  showMap && filters.mapParams
                    ? (productItems?.filter(
                        (x: companyagreementApi.ProductListItem) =>
                          !x.geoLocation ||
                          !context.portalConfiguration.showCaseConfig?.mapsConfig?.enforceProductBoundingBoxCheck ||
                          atlas.data.BoundingBox.containsPosition(
                            filters.mapParams!.currentPosition.boundingBox!,
                            convertToAtlasPosition(x.geoLocation),
                          ),
                      ) ?? [])
                    : productItems!,
              }}
              showMap={showMap}
              navigatorPosition={navigatorPosition}
              loadMoreProducts={(limit: number, offset: number) => {
                setFilters({
                  ...filters,
                  productsLimit: limit,
                  productsOffset: offset,
                })
              }}
              loadingProducts={products?.isLoading}
              loadingCategories={categories?.isLoading}
              categories={!filters?.searchText ? categoryItems : undefined}
              onCategorySelection={(categoryId: string) => {
                goToShowcase({
                  macroCategoryId,
                  serviceCategory: getServiceCategory(),
                  selectedCategory: categoryId,
                  breadCrumb: `${breadcrumbFromURL ? `${breadcrumbFromURL}::${categoryId}` : categoryId}`,
                })
              }}
              onProductSelection={(productId: string) =>
                goToShowcase({
                  macroCategoryId,
                  serviceCategory: getServiceCategory(),
                  breadCrumb: breadcrumbFromURL ?? '',
                  selectedProduct: productId,
                  selectedCategory: filters?.selectedCategory,
                })
              }
              parentSelectedCategoryItem={currentCategory.isSuccess ? currentCategory?.data : undefined}
            />
          </>
        ) : !categories.isLoading && !products.isLoading ? (
          <div className='mt-10 grid text-center font-bold'>{globalT.t('Global.Paginator.EmptyMessage')}</div>
        ) : (
          <div className='card justify-content-center flex' data-testid='ShowcaseLoadingSpinner'>
            <ProgressSpinner data-testid='loading-element' aria-label={globalT.t('Global.Loading.Message')} />
          </div>
        )}
      </PageWidget>
    )
}
